import clsx from 'clsx';
import {NEXT_PUBLIC_DISABLE_STATION_IMAGES} from 'config/index';
import {StationsContext} from 'context/StationsContext';
import {useTranslation} from 'hooks/translations/useCustomTranslation';
import Image from 'next/image';
import React, {FC, useContext, useState} from 'react';

import {SxProps} from '@mui/material';

import {Station} from 'lib/api/backend.schemas';
import {TypoComponentsStationInformation} from 'lib/api/strapi';

import {formatDate, formatTime} from 'utils/date/formatDate';

import {palette} from 'styles/mui/scss';

import {Box, Button, Grid, RemoteStationInformation, Typography} from 'components/basic-components';
import Breadcrumb from 'components/basic-components/Breadcrumb/Breadcrumb';
import StationLabels from 'components/basic-components/StationLabels';
import {StarcarMap} from 'components/basic-components/StationMap/StarcarMap';
import {withErrorBoundary} from 'components/error/ErrorBoundary';
import ContentWrapper from 'components/layout-components/ContentWrapper/ContentWrapper';
import BPRModal from 'components/section-components/bpr/BPRModal/BPRModal';

import StationFallbackImage from './StationFallbackImages/StationFallbackImage';
import styles from './StationInformation.module.scss';

export const BusinessHours = ({station, sx}: {station: Station; sx?: SxProps}) => {
  const {t} = useTranslation('common');
  if (!station) return null;

  return (
    <Box sx={{display: 'flex', margin: '20px 0', ...sx}}>
      <Box className={styles.businessHoursDaysWrapper}>
        <Typography>{t('moFr')}:</Typography>
        <Typography>{t('sa')}:</Typography>
        <Typography>{t('su')}:</Typography>
      </Box>
      <Box>
        {station?.businessHours.all[1].closed === false ? (
          <Typography>
            {formatTime(station?.businessHours.all[1].from)} -{' '}
            {formatTime(station?.businessHours.all[1].to)}
          </Typography>
        ) : (
          <Typography>{t('closed')}</Typography>
        )}
        {station?.businessHours.all[6].closed === false ? (
          <Typography>
            {formatTime(station?.businessHours.all[6].from)} -{' '}
            {formatTime(station?.businessHours.all[6].to)}
          </Typography>
        ) : (
          <Typography>{t('closed')}</Typography>
        )}
        {station?.businessHours.all[0].closed === false ? (
          <Typography>
            {formatTime(station?.businessHours.all[0].from)} -{' '}
            {formatTime(station?.businessHours.all[0].to)}
          </Typography>
        ) : (
          <Typography>{t('closed')}</Typography>
        )}
      </Box>
    </Box>
  );
};

export const Address = ({station, sx}: {station: Station; sx?: SxProps}) => {
  const {t} = useTranslation('common');
  if (!station) return null;

  return (
    <Box sx={{...sx}}>
      <Box>
        {station?.subName && <Typography>{station?.subName}</Typography>}
        <Typography>{station?.street}</Typography>
        <Typography>
          {station?.zip} {station?.city}
        </Typography>
      </Box>
      <Box>
        {station?.phone ? (
          <Typography>
            <a style={{color: 'inherit'}} href={`tel:${station.phone}`}>
              {t('telefon')}: {station?.phone}
            </a>
          </Typography>
        ) : null}
        {station?.fax ? (
          <Typography>
            {t('fax')}: {station?.fax}
          </Typography>
        ) : null}
        {station?.email ? (
          <Typography>
            <a style={{color: 'inherit'}} href={`mailto:${station.email}`}>
              {station.email}
            </a>
          </Typography>
        ) : null}
      </Box>
      <Box>
        {station.movingDate && (
          <>
            <Typography className={styles.additionalInfoHeadline}>
              {t('newAddressFrom')} {formatDate(station.movingDate)}
            </Typography>
            <Box component="address">
              <Typography>{station?.movingStreet}</Typography>
              <Typography>
                {station?.movingZip} {station?.movingCity}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export const StationInfo = ({
  station,
  handleCTA,
  titleVariant = 'large',
}: {
  station: Station;
  handleCTA?: () => void;
  titleVariant?: 'small' | 'large';
}) => {
  const {t} = useTranslation('common');

  return (
    <Grid container columnSpacing={'2%'} sx={{flexDirection: {md: 'row-reverse'}}}>
      <Grid xxs={12} md={6}>
        {station?.picture && !NEXT_PUBLIC_DISABLE_STATION_IMAGES ? (
          <Image
            src={station?.picture}
            height={500}
            width={500}
            alt="Baden-Baden"
            style={{marginTop: titleVariant === 'small' && 0}}
            className={styles.image}
          />
        ) : (
          <StationFallbackImage
            className={styles.image}
            style={{marginTop: titleVariant === 'small' && 0}}
          />
        )}
      </Grid>
      <Grid xxs={12} md={6}>
        <Typography
          className={clsx({
            [styles.title]: titleVariant === 'large',
            [styles.titleSmall]: titleVariant === 'small',
          })}
        >
          {station?.name}
        </Typography>
        <StationLabels sx={{paddingTop: '15px'}} station={station} />
        <Box sx={{color: palette.colorGrey80, '& p': {lineHeight: '24px'}}}>
          {station?.remoteStation ? (
            <RemoteStationInformation
              isSpecial={station?.specialRemoteStation || false}
              sx={{marginTop: '20px'}}
            />
          ) : null}
          {station?.remoteStation ? (
            <Grid container className={styles.gridContainer}>
              <Grid xxs={5.8}>
                <Typography fontWeight={700}>{t('meetingPoint')}</Typography>
                <Typography>{station?.subName}</Typography>
                <Address station={station} />
              </Grid>
              <Grid xxs={5.8}>
                <Typography fontWeight={700}>{t('relatedStation')}</Typography>
                <Typography fontWeight={700}>{station?.relatedStation?.description}</Typography>
                <Address station={station?.relatedStation} />
              </Grid>
            </Grid>
          ) : (
            <Address station={station} sx={{margin: '20px 0 10px'}} />
          )}
          {station?.remoteStation && station?.relatedStation ? (
            <BusinessHours station={station} sx={{marginTop: '10px'}} />
          ) : (
            <BusinessHours station={station} />
          )}
          {handleCTA ? (
            <Button onClick={handleCTA} className={styles.button}>
              {t('stationBookingButton')}
            </Button>
          ) : null}
        </Box>
      </Grid>
    </Grid>
  );
};

const StationInformation: FC<TypoComponentsStationInformation> = () => {
  const stations = useContext(StationsContext);
  const station = stations[0];

  const breadcrumb = [
    {
      linkText: `${station.name}`,
    },
  ];
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const handleCTA = () => {
    setModalOpen(true);
  };

  return (
    <ContentWrapper>
      <BPRModal modalOpen={modalOpen} setModalOpen={setModalOpen} presetStation={station?.id} />
      <Box className={styles.wrapper}>
        <Breadcrumb link={breadcrumb} />
        <Typography className={styles.titleMobile}>{station?.name}</Typography>
        <StationInfo station={station} handleCTA={handleCTA} />
        <Box sx={{marginBottom: {xxs: '20px', md: '80px'}}}>
          <StarcarMap stations={stations} onClickMarker={undefined} activeStation={undefined} />
        </Box>
      </Box>
    </ContentWrapper>
  );
};

export default withErrorBoundary(StationInformation, ' StationInformation');
